import Vue from 'vue';

export default Vue.component('ed-select-custom', { 
  name: 'ed-select-custom',
  props: {
    options: {
      type: Array as () => Array<string | number | { text: string; value: any }>,
      required: true,
    },
    value: {
      type: [String, Number, Object],
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    rules: {
      type: Array as () => Array<(value: any) => boolean | string>,
      default: () => [],
    },
    hint: {
      type: String,
      default: '',
    },
    persistentHint: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showValidationError: {
      type: Boolean,
      default: false,
    },
    showAsteristRequired: {
      type: Boolean,
      default: false,
    },
    itemText: {
      type: [String, Function],
      default: 'text',
    },
    itemValue: {
      type: [String, Function],
      default: 'value',
    },
  },
  data() {
    return {
      isOpen: false,
      isFocused: false,
      selectedValue: this.value,
      highlightedIndex: -1,
      errorMessage: '',
      windowIsFocused: true,
      bottomOffset: 50,
    };
  },
  computed: {
    hasError(): boolean {
      return this.errorMessage !== '';
    },
    shouldShowHint(): boolean {
      return (this.persistentHint || this.isFocused) && !this.hasError;
    },
    scrollHandlers(): {type: string, listener: any}[] {
      return [
        { type: 'wheel', listener: this.handleWheelEvent },
      ];
    },
    showValidationErrorActive(): boolean {
      return this.showValidationError && this.hasError;
    }
  },
  watch: {
    value(newValue) {
      this.selectedValue = newValue;
      this.validate();
    },
    isOpen() {
      this.setScrollHandlers(this.isOpen);
    },
  },
  methods: {
    selectOption(option: string | number | { text: string; value: any }) {
      if (this.disabled) return;
      this.selectedValue = this.getValue(option);
      this.$emit('input', this.selectedValue);
      this.$emit('change', this.selectedValue);
      this.hideGlobalDropdown();
      this.validate();
    },
    highlightIndex(index: number) {
      this.highlightedIndex = index;
    },
    highlightSelected() {
      const selectedValue = this.selectedValue;
      const index = this.options.findIndex(
        option => this.getValue(option) === selectedValue
      );
      this.highlightedIndex = index >= 0 ? index : -1;
      if (this.isOpen) {
        this.$edEventBus.$emit('update-dropdown-highlight', this.highlightedIndex);
      }
    },
    handleKeydown(event: KeyboardEvent) {
      if (this.disabled) return;
      if (!this.isOpen) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
        }
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.highlightedIndex =
            (this.highlightedIndex + 1) % this.options.length;
          this.$edEventBus.$emit('update-dropdown-highlight', this.highlightedIndex);
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.highlightedIndex =
            (this.highlightedIndex - 1 + this.options.length) %
            this.options.length;
          this.$edEventBus.$emit('update-dropdown-highlight', this.highlightedIndex);
          break;

        case 'Enter':
          event.preventDefault();
          if (this.highlightedIndex >= 0) {
            this.selectOption(this.options[this.highlightedIndex]);
          }
          break;

        case 'Escape':
          event.preventDefault();
          this.isOpen = false;
          this.hideGlobalDropdown();
          break;
      }
    },
    handleFocus() {
      if (this.disabled) return;
      this.isFocused = true;
    },
    handleBlur() {
      if (this.disabled) return;
      this.hideGlobalDropdown();
      this.isFocused = false;
      this.isOpen = false;
      this.validate();
    },
    validate() {
      for (const rule of this.rules) {
        const result = rule(this.selectedValue);
        if (result === false || typeof result === 'string') {
          this.errorMessage = typeof result === 'string' ? result : 'Invalid value.';
          return;
        }
      }
      this.errorMessage = '';
    },
    getDisplayText(option: any): string {
      if (typeof this.itemText === 'function') return this.itemText(option);
      return typeof option === 'object' ? option[this.itemText] : String(option);
    },
    getValue(option: any): any {
      if (typeof this.itemValue === 'function') return this.itemValue(option);
      return typeof option === 'object' ? option[this.itemValue] : option;
    },
    getDisplayTextFromValue(value: any): string {
      const option = this.options.find(opt => this.getValue(opt) === value);
      return option ? this.getDisplayText(option) : '';
    },
    isOptionSelected(option: any): boolean {
      return this.getValue(option) === this.selectedValue;
    },
    getDropDownList(): HTMLElement | null {
      return this.$el.querySelector('.custom-select-underlined__dropdown');
    },
    handleWheelEvent(event: WheelEvent): void {
      const dropdownList = this.getDropDownList();
      if (!this.isOpen || !dropdownList) {
        return;
      }

      if (!this.$el.contains(event.target)) {
        event.preventDefault();
        return;
      }

      const scroll = {
        isScrollingUp: event.deltaY < 0,
        isScrollingDown: event.deltaY > 0,
        isAtTop: dropdownList.scrollTop <= 0,
        isAtBottom: Math.abs(dropdownList.scrollHeight - dropdownList.clientHeight - dropdownList.scrollTop) < 1,
      };

      // Prevent scroll only at boundaries
      if ((scroll.isScrollingUp && scroll.isAtTop) || (scroll.isScrollingDown && scroll.isAtBottom)) {
        event.preventDefault();
      }
    },
    setScrollHandlers(turnOn: boolean): void {
      this.scrollHandlers?.forEach((handler: {type: string, listener: any}) => {
        if (turnOn) {
          window.addEventListener(handler.type, handler.listener, { passive: false });
        } else {
          window.removeEventListener(handler.type, handler.listener);
        }
      });
    },
    showGlobalDropdown() {
      if (this.disabled) return;
      
      const element = this.$refs.select as HTMLElement;
      const rect = element.getBoundingClientRect();
      this.isOpen = true;
      
      this.$edEventBus.$emit('show-select-dropdown', {
        options: this.options,
        selectedValue: this.selectedValue,
        position: {
          top: this.options?.length > 0 ? rect.bottom - this.bottomOffset : rect.bottom,
          left: rect.left,
          width: rect.width
        },
        itemText: this.itemText,
        itemValue: this.itemValue,
        selectCallback: (value: any) => {
          this.selectOption(value);
        }
      });

      setTimeout(() => document.addEventListener('click', this.handleBlur), 100);
    },
    hideGlobalDropdown() {
      this.isOpen = false;
      document.removeEventListener('click', this.handleBlur);
      this.$edEventBus.$emit('hide-select-dropdown');
    },
    updateDropdownPosition() {
      if (this.isOpen) {
        const element = this.$refs.select as HTMLElement;
        const rect = element.getBoundingClientRect();
        
        this.$edEventBus.$emit('update-dropdown-position', {
          top: rect.bottom - this.bottomOffset,
          left: rect.left,
          width: rect.width
        });
      }
    },
    focusWindow() {
      this.windowIsFocused = true;
    },
    unfocusWindow() {
      this.windowIsFocused = false;
    },
  },
  /* Hooks */
  mounted() {
    window.addEventListener('focus', this.focusWindow);
    window.addEventListener('blur', this.unfocusWindow);
    window.addEventListener('resize', this.updateDropdownPosition);
    window.addEventListener('scroll', this.updateDropdownPosition);
  },
  beforeDestroy() {
    this.setScrollHandlers(false);
    window.removeEventListener('focus', this.focusWindow);
    window.removeEventListener('blur', this.unfocusWindow);
    window.removeEventListener('resize', this.updateDropdownPosition);
    window.removeEventListener('scroll', this.updateDropdownPosition);
  }
});